import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { useAuthenticatedSession } from 'utils/sessionProvider';
import { EmojiPicker } from '../EmojiPicker';
import { ReactionButton_chat_createReaction_Mutation } from './__generated__/ReactionButton_chat_createReaction_Mutation.graphql';
import { ReactionButton_chat_messageFragment$key } from './__generated__/ReactionButton_chat_messageFragment.graphql';
import { useToast } from '@cardboard-ui/react';
import { t } from '@lingui/macro';

const messageFragment = graphql`
  fragment ReactionButton_chat_messageFragment on Message {
    id
    reactions {
      edges {
        node {
          id
          text
          senderId
          deletedAt
        }
      }
    }
  }
`;

const createReactionMutation = graphql`
  mutation ReactionButton_chat_createReaction_Mutation(
    $input: CreateReactionInput!
  ) {
    createReaction(input: $input) {
      id
      message {
        ...ReactionButton_chat_messageFragment
      }
    }
  }
`;

interface ReactionButtonProps {
  message: ReactionButton_chat_messageFragment$key;
  keepScrollAtTheBottom: () => void;
}

export const ReactionButton = ({
  message,
  keepScrollAtTheBottom,
}: ReactionButtonProps) => {
  const { id, reactions } = useFragment(messageFragment, message);
  const toast = useToast();
  const onErrorToast = ({ action }: { action: 'add' | 'remove' }) =>
    toast({
      status: 'error',
      position: 'top-right',
      title:
        action === 'add'
          ? t`Could not add your reaction`
          : t`Could not remove that reaction`,
    });

  const [createReaction] =
    useMutation<ReactionButton_chat_createReaction_Mutation>(
      createReactionMutation,
    );
  const { member: sessionMember } = useAuthenticatedSession();
  const reactionNodes = reactions.edges.map((e) => e.node);

  const handleEmojiPick = (emoji: string) => {
    const myReaction = reactionNodes.find(
      (n) =>
        !n.deletedAt && n.senderId === sessionMember.id && n.text === emoji,
    );

    if (myReaction) {
      // Already reacted this way? Great!
      return;
    }

    createReaction({
      variables: {
        input: {
          messageId: id,
          reaction: emoji,
        },
      },
      onCompleted: () => {
        keepScrollAtTheBottom();
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: (e) => {
        onErrorToast({ action: 'add' });
      },
    });
  };

  return <EmojiPicker onEmojiPick={handleEmojiPick} />;
};
